<template>
  <modal
    name="support-ticket-inbox"
    :width="1180"
    :height="760"
    :adaptive="true"
    :scrollable="false"
    class="support-inbox-modal modal-not-overflow"
    @before-open="prepare"
    @closed="reset"
  >
    <section class="support-inbox" :class="{ 'support-inbox--selected': selectedId }" aria-labelledby="support-inbox-title">
      <header class="support-inbox__header">
        <div><span>Support</span><h2 id="support-inbox-title">My support conversations</h2></div>
        <div class="support-inbox__actions">
          <app-button design="new" size="small" theme="dark" @click.native="goToMyTickets">Go to My Tickets</app-button>
          <app-button design="new" size="small" theme="success" @click.native="createTicket">Create new ticket</app-button>
          <app-modal-close design="new" label="Close support conversations" @click="close" />
        </div>
      </header>

      <div class="support-inbox__body">
        <aside class="support-inbox__sidebar">
          <header>
            <div><h3>Conversations</h3><span>{{ tickets.length }}</span></div>
            <button type="button" :disabled="loadingTickets" @click="loadTickets">{{ loadingTickets ? 'Loading…' : 'Refresh' }}</button>
          </header>
          <div v-if="loadingTickets && !tickets.length" class="support-inbox__state"><app-spinner design="new" /></div>
          <div v-else-if="listError && !tickets.length" class="support-inbox__state" role="alert">
            <strong>Unable to load tickets</strong><p>{{ listError }}</p>
            <app-button design="new" size="small" @click.native="loadTickets">Retry</app-button>
          </div>
          <div v-else-if="!tickets.length" class="support-inbox__state">
            <strong>No support conversations yet</strong><p>Create a ticket when you need help from Support.</p>
            <app-button design="new" size="small" theme="success" @click.native="createTicket">Create new ticket</app-button>
          </div>
          <div v-else class="support-inbox__tickets">
            <button
              v-for="item in tickets"
              :key="item.id"
              type="button"
              class="support-inbox-ticket"
              :class="{ 'support-inbox-ticket--active': selectedId === item.id, 'support-inbox-ticket--unread': item.is_unread }"
              @click="selectTicket(item)"
            >
              <span class="support-inbox-ticket__topline">
                <strong>{{ item.public_number }}</strong>
                <app-status-badge design="new" compact :label="statusLabel(item.status)" :tone="statusTone(item.status)" />
              </span>
              <span class="support-inbox-ticket__subject">{{ item.subject }}</span>
              <span class="support-inbox-ticket__footer">
                <span class="support-inbox-ticket__preview">{{ ticketPreview(item) }}</span>
                <span class="support-inbox-ticket__meta">
                  <i v-if="item.is_unread">Support replied</i>
                  <time :datetime="item.last_activity_at">{{ relativeTime(item.last_activity_at) }}</time>
                </span>
              </span>
            </button>
          </div>
        </aside>

        <main class="support-inbox__detail">
          <button v-if="selectedId" type="button" class="support-inbox__back" @click="clearSelection">← Conversations</button>
          <div v-if="!selectedId" class="support-inbox__empty-detail">
            <span aria-hidden="true">?</span><strong>Select a conversation</strong>
            <p>Choose a ticket to read its history and continue the chat with Support.</p>
          </div>
          <div v-else-if="loadingDetail" class="support-inbox__state"><app-spinner design="new" /></div>
          <div v-else-if="detailError && !ticket" class="support-inbox__state" role="alert">
            <strong>Unable to load this conversation</strong><p>{{ detailError }}</p>
            <app-button design="new" size="small" @click.native="reloadSelected">Retry</app-button>
          </div>
          <template v-else-if="ticket">
            <header class="support-inbox__ticket-header">
              <div><span>{{ ticket.public_number }}</span><h3 :title="ticket.subject">{{ ticket.subject }}</h3></div>
              <div>
                <app-status-badge design="new" compact :label="statusLabel(ticket.status)" :tone="statusTone(ticket.status)" />
                <span class="support-inbox__assignee">Support: <strong>{{ assigneeName }}</strong></span>
              </div>
            </header>
            <p v-if="detailError" class="support-inbox__error" role="alert">{{ detailError }}</p>
            <ticket-conversation-panel
              ref="conversation"
              class="support-inbox__conversation"
              :messages="messages"
              :message-count="messageCount"
              :requester-id="ticket.requester && ticket.requester.id"
              :has-older-messages="hasOlderMessages"
              :loading-messages="loadingMessages"
              :new-message="newMessage"
              :files="messageFiles"
              :closed="ticket.status === 'closed'"
              :working="working"
              @load-older="loadOlderMessages"
              @update:new-message="newMessage = $event"
              @select-files="selectMessageFiles"
              @remove-file="messageFiles.splice($event, 1)"
              @delete-attachment="deleteAttachment"
              @send="sendMessage"
            />
          </template>
        </main>
      </div>
    </section>
  </modal>
</template>

<script>
import AppModalClose from '@/components/AppModalClose'
import {
  deleteSupportTicketAttachment,
  getSupportTicket,
  getSupportTicketMessages,
  getSupportTickets,
  postSupportTicketMessage
} from '@/http'
import TicketConversationPanel from './TicketConversationPanel'
import { apiError, idempotencyKey, isStaleTicketError, normalizePage, relativeTime, statusLabel, statusTone } from '../ticketState'
import { appendSupportFiles, uploadSupportFiles } from '../supportFileUploads'

export default {
  name: 'SupportTicketInboxModal',
  components: { AppModalClose, TicketConversationPanel },
  props: { initialTicketId: { type: Number, default: null } },
  data() {
    return {
      tickets: [],
      selectedId: null,
      ticket: null,
      messages: [],
      messageCount: 0,
      messagePage: 1,
      hasOlderMessages: false,
      loadingTickets: false,
      loadingDetail: false,
      loadingMessages: false,
      working: false,
      listError: '',
      detailError: '',
      newMessage: '',
      messageFiles: [],
      selectionRequest: 0
    }
  },
  computed: {
    currentUserId() { return Number(this.$store.state.userData?.id) || null },
    assigneeName() { return this.ticket?.assignee?.full_name || 'Not assigned yet' }
  },
  methods: {
    relativeTime,
    statusLabel,
    statusTone,
    close() { this.$modal.hide('support-ticket-inbox') },
    prepare() { this.listError = ''; this.detailError = ''; this.loadTickets() },
    reset() {
      this.selectionRequest += 1
      this.selectedId = null
      this.ticket = null
      this.messages = []
      this.newMessage = ''
      this.messageFiles = []
    },
    ticketPreview(item) { return item.latest_message?.body || item.description_preview || 'No messages yet' },
    loadTickets() {
      if (!this.currentUserId) return Promise.resolve()
      this.loadingTickets = true
      this.listError = ''
      return getSupportTickets({ requester: this.currentUserId, page_size: 100 })
        .then(response => {
          this.tickets = normalizePage(response.data)
          const preferredId = this.initialTicketId || this.selectedId
          const preferred = this.tickets.find(item => item.id === preferredId)
          const next = preferred || this.tickets.find(item => item.is_unread) || this.tickets[0]
          if (next && (!this.ticket || this.selectedId !== next.id)) return this.selectTicket(next)
          if (!next) this.clearSelection()
          return null
        })
        .catch(error => { this.listError = apiError(error) })
        .finally(() => { this.loadingTickets = false })
    },
    selectTicket(item) {
      const ticketId = Number(item.id)
      const requestId = ++this.selectionRequest
      this.selectedId = ticketId
      this.ticket = null
      this.messages = []
      this.messageFiles = []
      this.newMessage = ''
      this.detailError = ''
      this.loadingDetail = true
      return Promise.all([getSupportTicket(ticketId), getSupportTicketMessages(ticketId, { page: 1, page_size: 50 })])
        .then(([ticketResponse, messageResponse]) => {
          if (requestId !== this.selectionRequest) return
          this.ticket = ticketResponse.data
          this.messages = normalizePage(messageResponse.data).slice().reverse()
          this.messageCount = messageResponse.data.count || this.messages.length
          this.messagePage = 1
          this.hasOlderMessages = Boolean(messageResponse.data.next)
          this.updateTicketSummary(this.ticket)
          this.$emit('read')
          this.$root.$emit('support-ticket-refresh')
          this.$nextTick(() => this.$refs.conversation?.scrollToBottom())
        })
        .catch(error => { if (requestId === this.selectionRequest) this.detailError = apiError(error) })
        .finally(() => { if (requestId === this.selectionRequest) this.loadingDetail = false })
    },
    reloadSelected() {
      const item = this.tickets.find(ticket => ticket.id === this.selectedId)
      if (item) this.selectTicket(item)
    },
    clearSelection() { this.selectionRequest += 1; this.selectedId = null; this.ticket = null; this.messages = [] },
    updateTicketSummary(ticket) {
      this.tickets = this.tickets.map(item => {
        if (item.id !== ticket.id) return item
        return { ...item, ...ticket, is_unread: false }
      })
    },
    loadOlderMessages() {
      if (!this.hasOlderMessages || this.loadingMessages || !this.ticket) return
      const previousHeight = this.$refs.conversation?.scrollHeight() || 0
      this.loadingMessages = true
      getSupportTicketMessages(this.ticket.id, { page: this.messagePage + 1, page_size: 50 })
        .then(response => {
          this.messagePage += 1
          this.messages = [...normalizePage(response.data).slice().reverse(), ...this.messages]
          this.hasOlderMessages = Boolean(response.data.next)
          this.$nextTick(() => this.$refs.conversation?.restoreScrollAfterPrepend(previousHeight))
        })
        .catch(error => { this.detailError = apiError(error) })
        .finally(() => { this.loadingMessages = false })
    },
    selectMessageFiles(input) {
      const incoming = Array.isArray(input) ? input : Array.from(input?.target?.files || [])
      const result = appendSupportFiles(this.messageFiles, incoming)
      this.messageFiles = result.files
      this.detailError = result.error
      if (input?.target) input.target.value = ''
    },
    async sendMessage() {
      if (!this.newMessage.trim() || this.working || !this.ticket) return
      this.working = true
      this.detailError = ''
      try {
        const uploaded = await uploadSupportFiles(this.messageFiles)
        const response = await postSupportTicketMessage(this.ticket.id, {
          body: this.newMessage.trim(),
          expected_state_version: this.ticket.state_version,
          attachments: uploaded.map(file => file.id)
        }, idempotencyKey())
        this.messages.push(response.data.message)
        this.messageCount += 1
        this.ticket = { ...this.ticket, ...response.data.ticket }
        this.newMessage = ''
        this.messageFiles = []
        this.updateTicketSummary(this.ticket)
        this.$emit('updated')
        this.$root.$emit('support-ticket-refresh')
        this.$nextTick(() => this.$refs.conversation?.scrollToBottom())
      } catch (error) {
        this.detailError = apiError(error)
        if (isStaleTicketError(error)) this.reloadSelected()
      } finally { this.working = false }
    },
    deleteAttachment(attachment) {
      if (!attachment?.can_delete || this.working) return
      this.working = true
      deleteSupportTicketAttachment(attachment.id).then(() => {
        this.messages = this.messages.map(message => ({
          ...message, attachments: (message.attachments || []).filter(item => item.id !== attachment.id)
        }))
        this.$notify({ type: 'success', text: 'Attachment removed.' })
      }).catch(error => { this.detailError = apiError(error) }).finally(() => { this.working = false })
    },
    createTicket() { this.close(); this.$emit('create') },
    goToMyTickets() { this.close(); this.$emit('navigate', this.selectedId) }
  }
}
</script>

<style lang="scss">
.support-inbox-modal > .vm--modal {
  height: 760px!important;
  overflow: hidden!important;
}
@media (max-height: 792px) {
  .support-inbox-modal > .vm--modal { height: calc(100vh - 32px)!important; }
}
</style>

<style lang="scss" scoped>
.support-inbox { display: flex; height: 100%; flex-direction: column; overflow: hidden; border-radius: 18px; background: $ui-surface-soft; color: $ui-text; font-family: $ui-font-body; }
.support-inbox__header { display: flex; min-height: 78px; flex: 0 0 auto; align-items: center; justify-content: space-between; gap: 20px; padding: 14px 18px 14px 22px; border-bottom: 1px solid $ui-line; background: $ui-surface; }
.support-inbox__header > div:first-child { display: grid; gap: 2px; }
.support-inbox__header > div:first-child span { color: $ui-blue; font-size: 12px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; }
.support-inbox__header h2 { margin: 0; color: $ui-ink; font: 600 22px/28px $ui-font-heading; }
.support-inbox__actions { display: flex; align-items: center; gap: 9px; }
.support-inbox__body { display: grid; min-height: 0; flex: 1; grid-template-columns: 330px minmax(0, 1fr); overflow: hidden; }
.support-inbox__sidebar { display: flex; min-height: 0; flex-direction: column; border-right: 1px solid $ui-line; background: $ui-surface; }
.support-inbox__sidebar > header { display: flex; min-height: 54px; align-items: center; justify-content: space-between; padding: 0 15px 0 18px; border-bottom: 1px solid $ui-line; }
.support-inbox__sidebar > header div { display: flex; align-items: baseline; gap: 7px; }
.support-inbox__sidebar h3 { margin: 0; color: $ui-ink; font: 600 15px $ui-font-heading; }
.support-inbox__sidebar > header span { color: $ui-muted; font-size: 13px; }
.support-inbox__sidebar > header button { padding: 5px; border: 0; background: transparent; color: $ui-blue; cursor: pointer; font-size: 12px; font-weight: 600; }
.support-inbox__tickets { min-height: 0; overflow-y: auto; overscroll-behavior: contain; }
.support-inbox-ticket { display: grid; width: 100%; gap: 6px; padding: 13px 15px 12px 18px; border: 0; border-bottom: 1px solid $ui-line; background: $ui-surface; color: $ui-text; cursor: pointer; font-family: $ui-font-body; text-align: left; }
.support-inbox-ticket:hover { background: $ui-surface-soft; }
.support-inbox-ticket--active { background: $ui-blue-soft; box-shadow: inset 3px 0 0 $ui-blue; }
.support-inbox-ticket--unread:not(.support-inbox-ticket--active) { box-shadow: inset 3px 0 0 $ui-red; }
.support-inbox-ticket:focus-visible { position: relative; z-index: 1; outline: 2px solid $ui-blue; outline-offset: -2px; }
.support-inbox-ticket__topline, .support-inbox-ticket__footer { display: flex; align-items: center; justify-content: space-between; gap: 9px; }
.support-inbox-ticket__topline > strong { color: $ui-blue; font-size: 11px; letter-spacing: .03em; }
.support-inbox-ticket__subject, .support-inbox-ticket__preview { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.support-inbox-ticket__subject { color: $ui-ink; font-size: 14px; font-weight: 700; }
.support-inbox-ticket__preview { min-width: 0; color: $ui-muted; font-size: 12px; }
.support-inbox-ticket__footer { min-height: 18px; color: $ui-muted; font-size: 11px; }
.support-inbox-ticket__meta { display: inline-flex; flex: 0 0 auto; align-items: center; gap: 6px; }
.support-inbox-ticket__meta i { padding: 3px 6px; border-radius: 8px; background: #fff1f2; color: $ui-red; font-style: normal; font-weight: 700; }
.support-inbox__detail { display: flex; min-width: 0; min-height: 0; flex-direction: column; padding: 14px; overflow: hidden; }
.support-inbox__ticket-header { display: flex; min-height: 52px; align-items: center; justify-content: space-between; gap: 16px; padding: 0 4px 12px; }
.support-inbox__ticket-header > div:first-child { min-width: 0; }
.support-inbox__ticket-header > div:first-child span { color: $ui-blue; font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
.support-inbox__ticket-header h3 { overflow: hidden; margin: 2px 0 0; color: $ui-ink; font: 600 18px/24px $ui-font-heading; text-overflow: ellipsis; white-space: nowrap; }
.support-inbox__ticket-header > div:last-child { display: flex; flex: 0 0 auto; align-items: center; gap: 10px; }
.support-inbox__assignee { color: $ui-muted; font-size: 12px; }
.support-inbox__assignee strong { color: $ui-text; }
.support-inbox__conversation { min-height: 0; flex: 1; }
.support-inbox__conversation :deep(.ticket-conversation__messages) { min-height: 0; flex: 1; overflow-y: auto; overscroll-behavior: contain; }
.support-inbox__state, .support-inbox__empty-detail { display: flex; min-height: 180px; flex: 1; align-items: center; justify-content: center; flex-direction: column; gap: 9px; padding: 24px; color: $ui-muted; text-align: center; }
.support-inbox__state strong, .support-inbox__empty-detail strong { color: $ui-ink; font-size: 15px; }
.support-inbox__state p, .support-inbox__empty-detail p { max-width: 340px; margin: 0; font-size: 13px; line-height: 1.45; }
.support-inbox__empty-detail > span { display: grid; width: 44px; height: 44px; place-items: center; border-radius: 50%; background: $ui-blue-soft; color: $ui-blue; font: 700 20px $ui-font-heading; }
.support-inbox__error { margin: 0 0 8px; padding: 8px 11px; border-radius: 8px; background: #fff1f2; color: $ui-red; font-size: 12px; }
.support-inbox__back { display: none; }
@media (max-width: 820px) {
  .support-inbox__header { min-height: 70px; padding-left: 16px; }
  .support-inbox__header h2 { font-size: 18px; }
  .support-inbox__actions :deep(.btn:first-child) { display: none; }
  .support-inbox__body { display: block; }
  .support-inbox__sidebar { height: 100%; border-right: 0; }
  .support-inbox__detail { display: none; height: 100%; padding: 10px; }
  .support-inbox--selected .support-inbox__sidebar { display: none; }
  .support-inbox--selected .support-inbox__detail { display: flex; }
  .support-inbox__back { display: inline-flex; width: max-content; padding: 3px 4px 7px; border: 0; background: transparent; color: $ui-blue; cursor: pointer; font-weight: 600; }
  .support-inbox__assignee { display: none; }
}
@media (max-width: 560px) {
  .support-inbox__header > div:first-child span { display: none; }
  .support-inbox__actions { gap: 5px; }
  .support-inbox__actions :deep(.btn) { padding-right: 8px; padding-left: 8px; font-size: 11px; }
}
</style>
